import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import * as React from 'react';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { toast } from '@/app/components/ui/use-toast';
import { REQUIRED_KEYS } from '../../../../../constants';
import type { PresetPromptValues } from '../types';
import { ValuesTag } from './ValuesTag';

interface PresetPromptsTagsProps {
  promptValues: PresetPromptValues;
  onDeleteTag?: (key: keyof PresetPromptValues, value: string) => void;
  onOpenPromptLibraryPopover?: () => void;
}

export const PresetPromptsTags: FC<PresetPromptsTagsProps> = ({
  promptValues,
  onDeleteTag,
  onOpenPromptLibraryPopover,
}) => {
  const t = useTranslations('create.proMode');

  const [hoveringGroup, setHoveringGroup] = React.useState<string | null>(null);

  const handleMouseEnter = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const tagGroup = target.closest('[data-tag-group]')?.getAttribute('data-tag-group');
    if (tagGroup) {
      setHoveringGroup(tagGroup);
    }
  };

  const handleMouseLeave = () => {
    setHoveringGroup(null);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const tagGroup = target.closest('[data-tag-group]')?.getAttribute('data-tag-group');
    if (tagGroup) {
      setHoveringGroup(tagGroup);
    }
  };

  const handleDeleteTag = (key: keyof typeof promptValues, value: string) => {
    // 检查是否允许：
    if (REQUIRED_KEYS.includes(key) && promptValues[key]?.length === 1 && promptValues[key]?.includes(value)) {
      toast({
        message: t('minSelect', { length: 1 }),
        type: 'error',
      });
      return;
    }

    onDeleteTag?.(key, value);
  };

  return (
    <div
      className="flex flex-wrap gap-y-1.5"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {Object.keys(promptValues).map((key, index) => (
        <ValuesTag
          key={key}
          groupKey={t(`${key as keyof PresetPromptValues}.title`)}
          tags={promptValues[key as keyof PresetPromptValues]}
          isFirst={index === 0}
          isHovering={hoveringGroup === t(`${key as keyof PresetPromptValues}.title`)}
          required={REQUIRED_KEYS.includes(key)}
          onClick={onOpenPromptLibraryPopover}
          onDelete={(tag) => handleDeleteTag(key as keyof typeof promptValues, tag)}
        />
      ))}
      <span
        className="flex cursor-pointer items-center justify-center rounded-8 bg-system-white10 px-2 py-1 text-base text-white hover:bg-system-white24"
        onClick={onOpenPromptLibraryPopover}
        data-tag-group="add"
        aria-label="Add"
      >
        <BaseLinePlus />
      </span>
    </div>
  );
};
